import router from "@/router";
import { useAuthStore } from "@/stores/auth";
import axios from "axios";

const baseUrl = process.env.VUE_APP_API_BASE_URL as string;

const axiosInstance = axios.create({
  baseURL: baseUrl,
  timeout: 5000,
  headers: {
    Authorization: localStorage.getItem("token")
      ? "JWT " + localStorage.getItem("token")
      : false,
    "Content-Type": "application/json",
    accept: "application/json",
  },
});

/** Add the token to every outgoing request */
axiosInstance.interceptors.request.use(function (config) {
  const token = localStorage.getItem("token");
  if (config.headers)
    config.headers.Authorization = token ? `JWT ${token}` : "";
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async function (error) {
    const originalRequest = error.config;

    // Handle network errors or undefined responses - don't logout, just reject
    if (!error.response) {
      console.log("Network error or no response");
      return Promise.reject(error);
    }

    // If refresh token request itself failed with invalid token, clear tokens and reject
    if (
      error.response.data?.code === "token_not_valid" &&
      error.response.status === 401 &&
      originalRequest.url === "/token/refresh/"
    ) {
      console.log("Refresh token is invalid, clearing tokens");
      localStorage.removeItem("token");
      localStorage.removeItem("refresh");
      return Promise.reject(error);
    }

    // If a regular request got a 401 with invalid token, try to refresh
    if (
      error.response.data?.code === "token_not_valid" &&
      error.response.status === 401 &&
      originalRequest.url !== "/token/refresh/"
    ) {
      const refreshToken = localStorage.getItem("refresh");
      if (refreshToken && refreshToken !== "undefined") {
        try {
          const tokenParts = JSON.parse(
            window.atob(refreshToken.split(".")[1])
          );
          const now = Math.ceil(Date.now() / 1000);

          if (tokenParts.exp > now) {
            console.log("Attempting to refresh token");
            try {
              const response = await axiosInstance.post("/token/refresh/", {
                refresh: refreshToken,
              });
              console.log("Token refreshed successfully");
              localStorage.setItem("token", response.data.access);
              axiosInstance.defaults.headers.common["Authorization"] =
                "JWT " + response.data.access;
              originalRequest.headers["Authorization"] =
                "JWT " + response.data.access;

              return axiosInstance(originalRequest);
            } catch (refreshErr) {
              console.log("Error refreshing token:", refreshErr);
              if ((refreshErr as any).response?.status === 401) {
                const auth = useAuthStore();
                auth.logout();
                router.push({ path: "/login", replace: true });
              }
              return Promise.reject(refreshErr);
            }
          } else {
            console.log("Refresh token has expired");
            const auth = useAuthStore();
            auth.logout();
            router.push({ path: "/login", replace: true });
            return Promise.reject(new Error("Refresh token expired"));
          }
        } catch (parseErr) {
          console.log("Error parsing refresh token:", parseErr);
          return Promise.reject(error);
        }
      } else {
        console.log("No refresh token available");
        router.push("/login");
        return Promise.reject(error);
      }
    }

    // For all other errors, just reject
    return Promise.reject(error);
  }
);

export const ManagerApi = {
  /**
   * Fetch the manager dashboard data including payment stats,
   * pending claims, and activity summary
   */
  fetchDashboard: async (): Promise<ManagerDashboardResponse> => {
    try {
      const response = await axiosInstance.get(`${baseUrl}manager/dashboard/`);
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  /**
   * Fetch contact requests assigned to the current user
   */
  fetchMyAssignments: async (): Promise<MyAssignmentsResponse> => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}manager/my-assignments/`
      );
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  /**
   * Approve a pending claim request
   */
  approveClaim: async (claimId: number): Promise<ClaimActionResponse> => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}manager/claims/${claimId}/approve/`
      );
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  /**
   * Deny a pending claim request
   */
  denyClaim: async (
    claimId: number,
    notes?: string
  ): Promise<ClaimActionResponse> => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}manager/claims/${claimId}/deny/`,
        { notes }
      );
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  /**
   * Fetch list of unpaid houses for the given year
   */
  fetchUnpaidHouses: async (
    year?: number,
    search?: string
  ): Promise<UnpaidHousesResponse> => {
    try {
      const params = new URLSearchParams();
      if (year) params.append("year", year.toString());
      if (search) params.append("search", search);

      const url = `${baseUrl}manager/unpaid-houses/${params.toString() ? "?" + params.toString() : ""}`;
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  /**
   * Record a check payment
   */
  recordCheckPayment: async (
    data: CheckPaymentRequest
  ): Promise<CheckPaymentResponse> => {
    try {
      const formData = new FormData();
      formData.append("house_id", data.house_id.toString());
      formData.append("amount", data.amount.toString());
      if (data.check_number) formData.append("check_number", data.check_number);
      if (data.dues_year) formData.append("dues_year", data.dues_year.toString());
      if (data.notes) formData.append("notes", data.notes);
      if (data.check_image) formData.append("check_image", data.check_image);

      const response = await axiosInstance.post(
        `${baseUrl}manager/payments/check/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.log(error);
      throw error;
    }
  },
};
